import { t } from "i18next";
import { validator } from "../../../services/form-validator/form-validator";
import { api } from "../../../services/api/api";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import { ServiceValidator } from "./service-validator";

export const ServiceFormValidator = {
    category: (e: EasyTech.PartCategory) => !e ? true : validator.isString(e.label),
    label: (e: unknown) => validator.isString(e),
    price: (e: unknown) => !e || validator.isString(e) || validator.isNumber(e),
    ref: (e: unknown) => !e || validator.isString(e),
    type: (e: unknown) => validator.isString(e)
};

/**
* ServiceForm: Create/edit service form 
* @param data - Data to edit
* @param onChange - On change event, trigger on field change
* @param onSubmit - On submit event, trigger enter key down on field
* @param submitted - is form submitted
*/
export default function ServiceForm({
    data,
    onChange,
    onSubmit,
    submitted
}: {
    data?: EasyTech.Service,
    onChange: (name: string, value: any) => void,
    onSubmit: () => void,
    submitted?: boolean
}) {
    const TYPE_OPTIONS = [
        { value: "SERVICE", label: "Service" },
        { value: "BOOKING_FEES", label: "Booking Fees" },
        { value: "COMBO", label: "Combo" }
    ];

    const loadCategoryOptions = (inputValue: string, callback: any) => {
        api.get("users/parts_categories").then((categories: any) => {
            const filtered = categories
                .filter((cat: any) =>
                    (cat.label && cat.label.toLowerCase().includes(inputValue.toLowerCase())) ||
                    (cat.ref && cat.ref.toLowerCase().includes(inputValue.toLowerCase()))
                )
                .map((cat: any) => ({ value: cat.id, label: cat.label, ref: cat.ref }));
            callback(filtered);
        });
    };

    const getTypeValue = (type: string) => {
        return TYPE_OPTIONS.find(opt => opt.value === type) || null;
    };

    const validateFormData = (formData: any): boolean => {
        if (!formData) return false;
        const validationErrors = ServiceValidator.validate(formData, t);
        return validationErrors.length === 0;
    };

    const handleSubmitWithValidation = () => {
        if (submitted && !validateFormData(data)) {
            console.warn("Validation failed - missing fields");
            return;
        }
        onSubmit();
    };

    return (
        <>
            <div className="field">
                <label htmlFor="label">{t("common:name")} *</label>
                <input type="text"
                    className={`form-field ${(submitted && data && !validator.isFieldValid(data, "label", ServiceFormValidator)) && "error"}`}
                    onChange={(value) => onChange("label", value.target.value)}
                    onKeyDown={(e) => e.key === "ENTER" && handleSubmitWithValidation()}
                    value={data?.label || ""} />
            </div>

            <div className="field">
                <label htmlFor="ref">{t("common:ref:label")}</label>
                <input type="text"
                    className={`form-field ${(submitted && data && !validator.isFieldValid(data, "ref", ServiceFormValidator)) && "error"}`}
                    onChange={(value) => onChange("ref", value.target.value)}
                    onKeyDown={(e) => e.key === "ENTER" && handleSubmitWithValidation()}
                    value={data?.ref || ""} />
            </div>

            <div className="field">
                <label htmlFor="price">{t("common:sellingPrice")}</label>
                <input type="number"
                    className={`form-field ${(submitted && data && !validator.isFieldValid(data, "price", ServiceFormValidator)) && "error"}`}
                    onChange={(value) => onChange("price", value.target.value)}
                    onKeyDown={(e) => e.key === "ENTER" && handleSubmitWithValidation()}
                    value={data?.price || ""} />
            </div>

            <div className="field">
                <label htmlFor="type">{t("common:type")} *</label>
                <Select
                    options={TYPE_OPTIONS}
                    value={data?.type ? getTypeValue(data.type) : null}
                    onChange={(option: any) => {
                        if (option) {
                            onChange("type", option.value);
                        } else {
                            onChange("type", "");
                        }
                    }}
                    placeholder={t("piece:select_type")}
                    isClearable
                    classNamePrefix="react-select"
                    className={`${(submitted && data && !validator.isFieldValid(data, "type", ServiceFormValidator)) ? "error" : ""}`}
                />
            </div>

            <div className="field">
                <label htmlFor="category">{t("common:category")}</label>
                <AsyncSelect
                    cacheOptions
                    defaultOptions
                    loadOptions={loadCategoryOptions}
                    value={
                        data?.category?.id
                            ? {
                                value: data.category.id,
                                label: data.category.label,
                                ref: data.category.ref,
                            }
                            : null
                    }
                    onChange={(option: any) => {
                        if (option) {
                            onChange("category", {
                                id: option.value,
                                label: option.label,
                                ref: option.ref,
                            });
                        } else {
                            onChange("category", {});
                        }
                    }}
                    placeholder={t("piece:select_category")}
                    isClearable
                />
            </div>
        </>
    );
}
